import {
  useState,
  useEffect,
} from 'react';
import {
  Building2,
  MapPin,
  Calendar,
  DollarSign,
  Clock,
  Users,
  TrendingDown,
} from 'lucide-react';
import type { SourcingEvent } from '../../types';

interface DetailsTabProps {readonly event: SourcingEvent;}

export function DetailsTab({ event }: DetailsTabProps) {
  const [timeRemaining, setTimeRemaining] = useState('');

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: event.currency,
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const isReverseAuction = event.eventType === 'reverse_auction';

  useEffect(() => {
    if (!isReverseAuction) return;

    const updateTimer = () => {
      const now = Date.now();
      const endTime = event.auctionEndTime ? new Date(event.auctionEndTime).getTime() : 0;
      const startTime = event.auctionStartTime ? new Date(event.auctionStartTime).getTime() : 0;

      if (now < startTime) {
        const diff = startTime - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeRemaining(`Starts in ${hours}h ${mins}m`);
      } else if (now < endTime) {
        const diff = endTime - now;
        const mins = Math.floor(diff / (1000 * 60));
        const secs = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeRemaining(`${mins}:${secs.toString().padStart(2, '0')} remaining`);
      } else {
        setTimeRemaining('Ended');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [isReverseAuction, event.auctionStartTime, event.auctionEndTime]);

  return (
    <div className="space-y-6">
      {isReverseAuction && (
        <div className="rounded-lg bg-orange-50 border border-orange-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-orange-600" />
              <span className="font-semibold text-orange-800">Reverse Auction</span>
            </div>
            {timeRemaining && (
              <span className="text-sm font-mono text-orange-600">{timeRemaining}</span>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm text-orange-700">Starting Price</p>
              <p className="font-semibold text-lg">{formatCurrency(event.startingPrice ?? 0)}</p>
            </div>
            <div>
              <p className="text-sm text-orange-700">Current Lowest</p>
              <p className="font-semibold text-lg text-green-600">
                {event.currentLowestBid ? formatCurrency(event.currentLowestBid) : 'No bids yet'}
              </p>
            </div>
            <div>
              <p className="text-sm text-orange-700">Total Bids</p>
              <p className="font-semibold text-lg">{event.totalBids ?? 0}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Event Information</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Delivery Site</p>
                <p className="font-medium">{event.deliverySiteName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Delivery Zone</p>
                <p className="font-medium">{event.deliveryZone}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Required Delivery Date</p>
                <p className="font-medium">{formatDate(event.requiredDeliveryDate)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Estimated Value</p>
                <p className="font-medium text-lg">{formatCurrency(event.estimatedValue)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Bidding Information</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Bid Deadline</p>
                <p className="font-medium">{formatDateTime(event.bidDeadline)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Total Bids Received</p>
                <p className="font-medium">{event.totalBids ?? 0}</p>
              </div>
            </div>
            {event.currentLowestBidder && (
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Current Leader</p>
                  <p className="font-medium">{event.currentLowestBidder}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {event.description && (
        <div className="rounded-lg bg-gray-50 border p-4">
          <p className="text-sm text-gray-500 mb-1">Description</p>
          <p className="text-gray-700">{event.description}</p>
        </div>
      )}
    </div>
  );
}
